import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAdminAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtEUR } from "@/lib/format";
import { CHECKLIST_SECTIONS, SABATINI_SECTION } from "@/lib/checklist";
import { computeCompensi, QUOTA_SEGNALATORE, CONSULENTE_SABATINI_NOME } from "@/lib/compensi";
import {
  LogOut,
  Search,
  Download,
  Trash2,
  FileArchive,
  ChevronDown,
  ChevronRight,
  Landmark,
  ClipboardList,
  FileText,
  RefreshCw,
  CheckCircle2,
  Circle,
  Users,
  Wallet,
  Briefcase,
  FileSpreadsheet,
  MessageCircle,
} from "lucide-react";

const MONTH_LABELS = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

// Genera le opzioni degli ultimi 18 mesi + 6 futuri (utili per pianificazione)
function monthOptions() {
  const now = new Date();
  const opts = [];
  for (let i = -6; i <= 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    opts.push({
      value: `${y}-${String(m).padStart(2, "0")}`,
      label: `${MONTH_LABELS[m]} ${y}`,
    });
  }
  return opts;
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const { user, logout } = useAdminAuth();
  const [dossiers, setDossiers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterSab, setFilterSab] = useState("all"); // all|yes|no
  const [expanded, setExpanded] = useState({});
  const [expandedClient, setExpandedClient] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [segnalatori, setSegnalatori] = useState([]);
  // Filtro mese (YYYY-MM). "all" = nessun filtro periodo. Default: mese corrente.
  const [monthFilter, setMonthFilter] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (user === null) nav("/admin/login");
    else if (user && user.role !== "admin") {
      toast.error("Area riservata all'amministratore");
      nav("/");
    }
  }, [user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (filterSab === "yes") params.sabatini = true;
      if (filterSab === "no") params.sabatini = false;
      // Filtro mese: applica date_from/date_to inclusivi
      if (monthFilter && monthFilter !== "all") {
        const [y, m] = monthFilter.split("-").map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        params.date_from = `${y}-${String(m).padStart(2, "0")}-01T00:00:00`;
        params.date_to = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}T23:59:59`;
      }
      const [dRes, sRes, segRes] = await Promise.all([
        api.get("/admin/dossiers", { params }),
        api.get("/admin/stats"),
        api.get("/admin/segnalatori"),
      ]);
      setDossiers(dRes.data || []);
      setStats(sRes.data);
      setSegnalatori(segRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filterSab, monthFilter]);

  // Raggruppa i dossier per cliente (user_id o, se mancante, ragione_sociale)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const d of dossiers) {
      const cli = d.cliente || {};
      const key = d.user_id || cli.ragione_sociale || d.id;
      if (!map.has(key)) {
        map.set(key, {
          key,
          ragione_sociale: cli.ragione_sociale || "— anonimo —",
          email: cli.email || "",
          telefono: cli.telefono || "",
          dossiers: [],
        });
      }
      map.get(key).dossiers.push(d);
    }
    // Ordina i clienti per data più recente della loro pratica
    return Array.from(map.values()).sort((a, b) => {
      const la = Math.max(...a.dossiers.map((d) => new Date(d.created_at).getTime()));
      const lb = Math.max(...b.dossiers.map((d) => new Date(d.created_at).getTime()));
      return lb - la;
    });
  }, [dossiers]);

  // Somme aggregate per cliente
  const clientTotals = (client) => {
    let incassoStudio = 0;
    let quotaTommaso = 0;
    let quotaSegnalatore = 0;
    let compensoRevisore = 0;
    for (const d of client.dossiers) {
      const c = computeCompensi(d);
      if (!c) continue;
      incassoStudio += c.incassoStudioNetto;
      quotaTommaso += c.quotaTommaso;
      quotaSegnalatore += c.quotaSegnalatore;
      compensoRevisore += c.compensoRevisore;
    }
    return { incassoStudio, quotaTommaso, quotaSegnalatore, compensoRevisore };
  };

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare definitivamente il dossier e tutti i suoi documenti?")) return;
    try {
      await api.delete(`/admin/dossiers/${id}`);
      toast.success("Dossier eliminato");
      setDossiers((prev) => prev.filter((d) => d.id !== id));
      setSelected((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const onBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Eliminare definitivamente ${ids.length} dossier selezionat${ids.length === 1 ? "o" : "i"} e tutti i relativi documenti?`)) return;
    try {
      const { data } = await api.post("/admin/dossiers/bulk-delete", { ids });
      toast.success(`${data.deleted} dossier eliminat${data.deleted === 1 ? "o" : "i"}`);
      setDossiers((prev) => prev.filter((d) => !selected.has(d.id)));
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const toggleSelected = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === dossiers.length && dossiers.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(dossiers.map((d) => d.id)));
    }
  };

  const downloadZip = (id) => {
    // Cookie auth → aprire l'URL apre lo ZIP con l'header Content-Disposition
    window.location.href = `${API}/admin/dossiers/${id}/zip`;
  };

  const onExportCompensi = async () => {
    if (monthFilter === "all") {
      toast.error("Seleziona un mese per esportare i compensi");
      return;
    }
    const [y, m] = monthFilter.split("-").map(Number);
    setExporting(true);
    try {
      const res = await api.get("/admin/compensi/export", {
        params: { year: y, month: m },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const mese = MONTH_LABELS[m];
      const link = document.createElement("a");
      link.href = url;
      link.download = `compensi_${mese}_${y}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export completato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setExporting(false);
    }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  if (user === undefined) {
    return <div className="max-w-7xl mx-auto p-10 text-ink-muted">Caricamento...</div>;
  }
  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
            <ClipboardList className="w-3 h-3" /> Pannello amministratore
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-ink" data-testid="admin-title">
            Dossier <span className="text-brand">clienti</span>
          </h1>
          <p className="mt-2 text-ink-muted text-sm">
            Connesso come <span className="font-mono text-ink">{user.email}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/chat-stats"
            data-testid="link-chat-stats"
            className="inline-flex items-center gap-2 h-10 px-4 border border-line rounded text-sm hover:bg-brand hover:text-white hover:border-brand"
          >
            <MessageCircle className="w-4 h-4" /> Domande
          </Link>
          <Link
            to="/admin/segnalatori"
            data-testid="link-segnalatori"
            className="inline-flex items-center gap-2 h-10 px-4 border border-line rounded text-sm hover:bg-brand hover:text-white hover:border-brand"
          >
            <Users className="w-4 h-4" /> Segnalatori
          </Link>
          <button
            onClick={async () => {
              await logout();
              nav("/admin/login");
            }}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 h-10 px-4 border border-line rounded text-sm hover:bg-line/40"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Dossier totali" value={stats?.total ?? "—"} />
        <StatCard label="Con documenti caricati" value={stats?.with_documents ?? "—"} />
        <StatCard label="Con Sabatini" value={stats?.sabatini ?? "—"} />
      </div>

      {/* Filters */}
      <form
        onSubmit={onSearch}
        className="mt-8 bg-white border border-line rounded-lg shadow-card p-4 flex flex-col md:flex-row md:flex-wrap gap-3 items-stretch md:items-center"
      >
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per ragione sociale, email, telefono o ID"
            data-testid="admin-search"
            className="w-full h-11 pl-10 pr-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
          />
        </div>
        <select
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
          data-testid="admin-filter-month"
          className="h-11 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
        >
          <option value="all">Tutti i mesi</option>
          {monthOptions().map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={filterSab}
          onChange={(e) => setFilterSab(e.target.value)}
          data-testid="admin-filter-sab"
          className="h-11 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
        >
          <option value="all">Tutti i dossier</option>
          <option value="yes">Solo con Sabatini</option>
          <option value="no">Senza Sabatini</option>
        </select>
        <button
          type="submit"
          data-testid="admin-search-btn"
          className="h-11 px-5 bg-brand text-white rounded-md font-display uppercase tracking-widest text-sm hover:bg-brand-dark flex items-center gap-2"
        >
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Cerca
        </button>
        <button
          type="button"
          onClick={onExportCompensi}
          disabled={exporting || monthFilter === "all"}
          data-testid="export-compensi-btn"
          title={monthFilter === "all" ? "Seleziona un mese per esportare i compensi" : "Esporta compensi del mese in Excel"}
          className="h-11 px-5 border border-brand text-brand rounded-md font-display uppercase tracking-widest text-sm hover:bg-brand hover:text-white flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {exporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          Export compensi
        </button>
      </form>

      {/* Table */}
      <div className="mt-6 bg-white border border-line rounded-lg shadow-card overflow-hidden">
        {selected.size > 0 && (
          <div className="px-4 py-3 bg-brand/[0.08] border-b border-brand/30 flex items-center justify-between gap-3" data-testid="bulk-bar">
            <div className="text-sm text-ink">
              <span className="font-semibold text-brand">{selected.size}</span> dossier selezionat{selected.size === 1 ? "o" : "i"}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected(new Set())}
                data-testid="bulk-clear"
                className="text-sm text-ink-muted hover:text-ink px-3 h-9"
              >
                Annulla selezione
              </button>
              <button
                onClick={onBulkDelete}
                data-testid="bulk-delete"
                className="inline-flex items-center gap-2 h-9 px-4 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4" /> Elimina selezionati
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="p-10 text-center text-ink-muted">Caricamento...</div>
        ) : grouped.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">Nessun dossier trovato.</div>
        ) : (
          <div className="divide-y divide-line" data-testid="clients-grouped">
            {grouped.map((client) => {
              const tot = clientTotals(client);
              const isCliOpen = !!expandedClient[client.key];
              return (
                <div key={client.key} className="bg-white">
                  {/* Header cliente */}
                  <button
                    onClick={() => setExpandedClient((e) => ({ ...e, [client.key]: !e[client.key] }))}
                    className="w-full px-4 py-4 flex items-start gap-3 hover:bg-line/30 text-left"
                    data-testid={`client-toggle-${client.key}`}
                  >
                    <span className="mt-1 text-ink-muted">
                      {isCliOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="font-display text-lg text-ink truncate">{client.ragione_sociale}</span>
                        <span className="text-[11px] font-mono uppercase tracking-widest text-brand bg-brand/10 px-2 py-0.5 rounded">
                          {client.dossiers.length} pratic{client.dossiers.length === 1 ? "a" : "he"}
                        </span>
                      </div>
                      {(client.email || client.telefono) && (
                        <div className="mt-1 text-xs text-ink-muted">
                          {client.email && <span>{client.email}</span>}
                          {client.email && client.telefono && <span className="mx-2">·</span>}
                          {client.telefono && <span>{client.telefono}</span>}
                        </div>
                      )}
                    </div>
                    <div className="hidden md:flex flex-col items-end gap-0.5 min-w-[140px] shrink-0">
                      <div className="text-[10px] uppercase tracking-wider text-ink-muted">Studio (netto)</div>
                      <div className="font-mono text-sm text-brand font-semibold">{fmtEUR(tot.incassoStudio)}</div>
                    </div>
                  </button>

                  {/* Pratiche del cliente */}
                  {isCliOpen && (
                    <div className="bg-line/10 border-t border-line" data-testid={`client-body-${client.key}`}>
                      {client.dossiers.map((d) => (
                        <DossierRow
                          key={d.id}
                          d={d}
                          expanded={!!expanded[d.id]}
                          onToggle={() => toggle(d.id)}
                          selected={selected.has(d.id)}
                          onSelect={() => toggleSelected(d.id)}
                          onDelete={() => onDelete(d.id)}
                          onDownloadZip={() => downloadZip(d.id)}
                          segnalatori={segnalatori}
                          onDossierChanged={(patched) => setDossiers((prev) => prev.map((x) => (x.id === patched.id ? { ...x, ...patched } : x)))}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-ink-muted">
        <Link to="/" className="hover:text-brand">← Torna al sito</Link>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-line rounded-lg p-5 shadow-card">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-2 font-display text-3xl text-ink">{value}</div>
    </div>
  );
}

function progressFromSections(sections, docsByKey) {
  const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);
  const coveredItems = sections.reduce(
    (sum, s) => sum + s.items.filter((it) => (docsByKey[it.key] || []).length > 0).length,
    0,
  );
  const pct = totalItems > 0 ? Math.round((coveredItems / totalItems) * 100) : 0;
  return { totalItems, coveredItems, pct };
}

function checklistProgress(d) {
  const docsByKey = (d.documents || []).reduce((acc, doc) => {
    const k = doc.item_key || "_altro";
    (acc[k] = acc[k] || []).push(doc);
    return acc;
  }, {});
  const iper = progressFromSections(CHECKLIST_SECTIONS, docsByKey);
  const sab = d.sabatini ? progressFromSections([SABATINI_SECTION], docsByKey) : null;
  // Combinato (retro-compatibile per righe compatte)
  const sections = d.sabatini ? [...CHECKLIST_SECTIONS, SABATINI_SECTION] : CHECKLIST_SECTIONS;
  const combined = progressFromSections(sections, docsByKey);
  return { ...combined, sections, docsByKey, iper, sab };
}

function MiniProgress({ label, data, testid }) {
  const { coveredItems, totalItems, pct } = data;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="text-ink-muted">{label}</span>
        <span className="font-mono text-ink">{coveredItems}/{totalItems} · {pct}%</span>
      </div>
      <div className="mt-0.5 h-1.5 bg-line rounded-full overflow-hidden">
        <div className={`h-full transition-all ${pct === 100 ? "bg-green-600" : "bg-brand"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DossierRow({ d, expanded, onToggle, selected, onSelect, onDelete, onDownloadZip, segnalatori, onDossierChanged }) {
  const { iper, sab } = checklistProgress(d);
  const comp = computeCompensi(d);
  const nDocs = (d.documents || []).length;

  return (
    <Fragment>
      <div className={`px-4 py-3 flex items-start gap-3 border-b border-line last:border-b-0 ${selected ? "bg-brand/[0.05]" : "hover:bg-white"}`} data-testid={`prat-row-${d.id}`}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          data-testid={`select-${d.id}`}
          className="mt-1 w-4 h-4 accent-brand cursor-pointer"
        />
        <button onClick={onToggle} data-testid={`toggle-${d.id}`} className="mt-0.5 text-ink-muted hover:text-brand shrink-0">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3 items-center min-w-0">
          {/* ID + tipo */}
          <div className="md:col-span-3 min-w-0">
            <div className="flex items-center gap-2">
              {d.sabatini ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand/10 text-brand rounded text-[10px] uppercase tracking-wider">
                  <Landmark className="w-3 h-3" /> Iper + Sabatini
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-line text-ink-muted rounded text-[10px] uppercase tracking-wider">
                  <ClipboardList className="w-3 h-3" /> Iperammortamento
                </span>
              )}
            </div>
            <div className="mt-1 font-mono text-[11px] text-ink-muted truncate">ID {d.id.slice(0, 8)}…</div>
            <div className="text-[11px] text-ink-muted">{new Date(d.created_at).toLocaleDateString("it-IT")}</div>
          </div>
          {/* Progresso checklist: separato Iper + Sabatini */}
          <div className="md:col-span-4 min-w-0 space-y-1.5" data-testid={`prat-progress-${d.id}`}>
            <MiniProgress label="Iper" data={iper} testid={`prat-progress-iper-${d.id}`} />
            {sab && <MiniProgress label="Sabatini" data={sab} testid={`prat-progress-sab-${d.id}`} />}
            <div className="text-[11px] text-ink-muted">{nDocs} file caricat{nDocs === 1 ? "o" : "i"}</div>
          </div>
          {/* Compensi mini */}
          <div className="md:col-span-4 min-w-0 grid grid-cols-3 gap-2 text-[11px]">
            <div>
              <div className="text-ink-muted">Studio netto</div>
              <div className="font-mono text-brand font-semibold">{comp ? fmtEUR(comp.incassoStudioNetto) : "—"}</div>
            </div>
            <div>
              <div className="text-ink-muted">Tommaso</div>
              <div className="font-mono text-ink">{comp ? fmtEUR(comp.quotaTommaso) : "—"}</div>
            </div>
            <div>
              <div className="text-ink-muted">Segnalatore</div>
              <div className="font-mono text-ink">{comp && comp.quotaSegnalatore > 0 ? fmtEUR(comp.quotaSegnalatore) : "—"}</div>
            </div>
          </div>
          {/* Azioni */}
          <div className="md:col-span-1 flex items-center gap-2 justify-end">
            <button
              onClick={onDownloadZip}
              data-testid={`download-zip-${d.id}`}
              title="Scarica tutti i documenti (ZIP)"
              disabled={nDocs === 0}
              className="p-2 border border-line rounded hover:bg-brand hover:text-white hover:border-brand disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileArchive className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              data-testid={`delete-${d.id}`}
              title="Elimina pratica"
              className="p-2 border border-line rounded hover:bg-red-600 hover:text-white hover:border-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="bg-line/20 px-4 py-4 border-b border-line" data-testid={`prat-details-${d.id}`}>
          <DossierDetails d={d} segnalatori={segnalatori} onDossierChanged={onDossierChanged} />
        </div>
      )}
    </Fragment>
  );
}

function DossierDetails({ d, segnalatori = [], onDossierChanged }) {
  const calc = d.calc_snapshot;
  const cli = d.cliente || {};
  const documents = d.documents || [];

  // Group uploaded docs by item_key
  const docsByKey = documents.reduce((acc, doc) => {
    const k = doc.item_key || "_altro";
    (acc[k] = acc[k] || []).push(doc);
    return acc;
  }, {});

  // Progressi separati per agevolazione
  const iperProgress = progressFromSections(CHECKLIST_SECTIONS, docsByKey);
  const sabProgress = d.sabatini ? progressFromSections([SABATINI_SECTION], docsByKey) : null;

  // "orfani": documenti caricati con item_key non riconosciuta rispetto alle sezioni attive
  const activeSections = d.sabatini ? [...CHECKLIST_SECTIONS, SABATINI_SECTION] : CHECKLIST_SECTIONS;
  const knownKeys = new Set(activeSections.flatMap((s) => s.items.map((it) => it.key)));
  const orphanDocs = documents.filter((doc) => !knownKeys.has(doc.item_key));

  return (
    <div className="space-y-4">
      {/* Header row: Cliente + Calcolo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-line rounded p-4">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">Cliente</div>
          <div className="text-sm space-y-1">
            <div><span className="text-ink-muted">Ragione sociale:</span> <span className="text-ink">{cli.ragione_sociale || "—"}</span></div>
            <div><span className="text-ink-muted">Email:</span> <span className="text-ink">{cli.email || "—"}</span></div>
            <div><span className="text-ink-muted">Telefono:</span> <span className="text-ink">{cli.telefono || "—"}</span></div>
            {cli.note && <div className="pt-2 border-t border-line mt-2"><span className="text-ink-muted">Note:</span> <span className="text-ink">{cli.note}</span></div>}
            <div className="pt-2 border-t border-line mt-2 font-mono text-[11px] text-ink-muted break-all">ID: {d.id}</div>
          </div>
        </div>
        <div className="bg-white border border-line rounded p-4">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">Calcolo</div>
          {calc ? (
            <div className="text-sm space-y-1">
              <div><span className="text-ink-muted">Investimento:</span> <span className="text-ink font-mono">{fmtEUR(calc.input?.investimento)}</span></div>
              <div><span className="text-ink-muted">Tipologia:</span> <span className="text-ink">{calc.input?.tipologia}</span></div>
              <div><span className="text-ink-muted">Sabatini:</span> <span className="text-ink">{calc.input?.sabatini}</span></div>
              <div><span className="text-ink-muted">Benef. Iper:</span> <span className="text-ink font-mono">{fmtEUR(calc.beneficio_iperammortamento)}</span></div>
              <div><span className="text-ink-muted">Benef. Sabatini:</span> <span className="text-ink font-mono">{fmtEUR(calc.sabatini_beneficio)}</span></div>
              <div className="pt-2 border-t border-line mt-2"><span className="text-ink-muted">Vantaggio totale:</span> <span className="text-brand font-mono font-semibold">{fmtEUR(calc.vantaggio_complessivo)}</span></div>
            </div>
          ) : (
            <div className="text-ink-muted text-sm">Nessun calcolo allegato al dossier.</div>
          )}
        </div>
      </div>

      {/* Compensi & Segnalatore */}
      <CompensiBlock d={d} segnalatori={segnalatori} onDossierChanged={onDossierChanged} />

      {/* Checklist a tab (chiuse di default, si aprono cliccando sul tab) */}
      <ChecklistTabs
        dossierId={d.id}
        docsByKey={docsByKey}
        iperProgress={iperProgress}
        sabProgress={sabProgress}
        hasSabatini={!!d.sabatini}
      />

      {/* Documenti orfani (item_key non riconosciuta) */}
      {orphanDocs.length > 0 && (
        <div className="border border-amber-300 bg-amber-50/50 rounded" data-testid={`orphan-docs-${d.id}`}>
          <div className="px-3 py-2 flex items-center justify-between">
            <div className="text-sm font-semibold text-amber-900">Altri documenti caricati</div>
            <div className="text-xs font-mono text-amber-700">{orphanDocs.length}</div>
          </div>
          <ul className="divide-y divide-amber-200">
            {orphanDocs.map((doc) => (
              <li key={doc.doc_id} className="px-3 py-2 flex items-center gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-amber-700 flex-shrink-0" />
                <a
                  href={`${API}/admin/dossiers/${d.id}/doc/${doc.doc_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ink hover:text-brand truncate flex-1"
                >
                  {doc.filename}
                </a>
                <span className="text-ink-muted font-mono">[{doc.item_key || "n/a"}]</span>
                <a
                  href={`${API}/admin/dossiers/${d.id}/doc/${doc.doc_id}`}
                  className="text-ink-muted hover:text-brand"
                  title="Scarica"
                >
                  <Download className="w-3.5 h-3.5" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function CompensiBlock({ d, segnalatori, onDossierChanged }) {
  const comp = computeCompensi(d);
  const [segId, setSegId] = useState(d.segnalatore_id || "");
  const [segAttivo, setSegAttivo] = useState(!!d.segnalatore_attivo);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSegId(d.segnalatore_id || "");
    setSegAttivo(!!d.segnalatore_attivo);
  }, [d.segnalatore_id, d.segnalatore_attivo]);

  const save = async (nextId, nextAttivo) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/dossiers/${d.id}`, {
        segnalatore_id: nextId,
        segnalatore_attivo: nextAttivo,
      });
      onDossierChanged && onDossierChanged(data);
      toast.success("Aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onChangeSeg = (val) => {
    setSegId(val);
    const nextAttivo = !!val && segAttivo;
    save(val, nextAttivo);
  };

  const onToggleAttivo = () => {
    if (!segId) {
      toast.error("Seleziona prima un segnalatore");
      return;
    }
    const next = !segAttivo;
    setSegAttivo(next);
    save(segId, next);
  };

  return (
    <div className="bg-white border border-line rounded p-4" data-testid={`compensi-${d.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="w-4 h-4 text-brand" />
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Compensi pratica</div>
      </div>
      {comp ? (
        <div className={`grid grid-cols-1 gap-3 text-sm ${comp.quotaCappelletti > 0 ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
          <div className="border border-line rounded p-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">Fascia</div>
            <div className="mt-1 font-display text-lg text-ink">{comp.fascia.key}</div>
            <div className="text-[11px] text-ink-muted">{comp.fascia.label}</div>
          </div>
          <div className="border border-line rounded p-3 bg-brand/5">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">Incasso Studio (netto)</div>
            <div className="mt-1 font-mono text-lg text-brand font-semibold">{fmtEUR(comp.incassoStudioNetto)}</div>
            <div className="text-[11px] text-ink-muted">Lordo {fmtEUR(comp.incassoStudioLordo)} <span className="opacity-60">(incl. revisore {fmtEUR(comp.compensoRevisore)})</span></div>
          </div>
          <div className="border border-line rounded p-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1">
              <Briefcase className="w-3 h-3" /> Quota Tommaso <span className="text-ink-muted/60 normal-case tracking-normal">(di cui)</span>
            </div>
            <div className="mt-1 font-mono text-lg text-ink">{fmtEUR(comp.quotaTommaso)}</div>
            {comp.quotaSegnalatore > 0 && (
              <div className="text-[11px] text-ink-muted">−{fmtEUR(QUOTA_SEGNALATORE)} segnalatore (di cui)</div>
            )}
          </div>
          {comp.quotaCappelletti > 0 && (
            <div className="border border-line rounded p-3" data-testid={`cappelletti-${d.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted">Consulente Sabatini</div>
              <div className="mt-1 font-mono text-lg text-ink-muted">{fmtEUR(comp.quotaCappelletti)}</div>
              <div className="text-[11px] text-ink-muted truncate" title={CONSULENTE_SABATINI_NOME}>{CONSULENTE_SABATINI_NOME}</div>
              <div className="text-[10px] text-ink-muted/70 mt-1">Pass-through</div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-ink-muted text-sm">Nessun calcolo allegato: impossibile stimare i compensi.</div>
      )}

      {/* Segnalatore selector */}
      <div className="mt-4 pt-4 border-t border-line grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">
            Segnalatore
          </label>
          <select
            value={segId}
            onChange={(e) => onChangeSeg(e.target.value)}
            disabled={saving}
            data-testid={`seg-select-${d.id}`}
            className="w-full h-10 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm disabled:opacity-60"
          >
            <option value="">— Nessuno —</option>
            {segnalatori.map((s) => (
              <option key={s.id} value={s.id}>{s.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Flag attivo</label>
          <button
            onClick={onToggleAttivo}
            disabled={saving || !segId}
            data-testid={`seg-toggle-${d.id}`}
            className={`w-full h-10 rounded-md text-sm font-display uppercase tracking-widest border transition
              ${segAttivo
                ? "bg-brand text-white border-brand hover:bg-brand-dark"
                : "bg-white text-ink border-line hover:border-brand hover:text-brand"}
              disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {segAttivo ? `Attivo · −${fmtEUR(QUOTA_SEGNALATORE)}` : "Disattivo"}
          </button>
        </div>
      </div>
      {segnalatori.length === 0 && (
        <div className="mt-3 text-[11px] text-ink-muted">
          Nessun segnalatore in anagrafica. <Link to="/admin/segnalatori" className="text-brand hover:underline">Aggiungine uno</Link>.
        </div>
      )}
    </div>
  );
}


function ChecklistTabs({ dossierId, docsByKey, iperProgress, sabProgress, hasSabatini }) {
  // null = nessun tab aperto (default: solo i tab visibili col progresso).
  const [openTab, setOpenTab] = useState(null);

  const tabs = [
    { key: "iper", label: "Iperammortamento", progress: iperProgress, sections: CHECKLIST_SECTIONS },
  ];
  if (hasSabatini && sabProgress) {
    tabs.push({ key: "sabatini", label: "Nuova Sabatini", progress: sabProgress, sections: [SABATINI_SECTION] });
  }

  return (
    <div className="bg-white border border-line rounded" data-testid={`checklist-tabs-${dossierId}`}>
      {/* Tab strip (una accanto all'altra) */}
      <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-line">
        {tabs.map((t) => {
          const isOpen = openTab === t.key;
          const { coveredItems, totalItems, pct } = t.progress;
          const complete = pct === 100;
          return (
            <button
              key={t.key}
              onClick={() => setOpenTab(isOpen ? null : t.key)}
              data-testid={`checklist-tab-${t.key}-${dossierId}`}
              className={`flex-1 text-left px-4 py-3 transition-all ${
                isOpen ? "bg-brand/5 border-b-2 border-brand" : "hover:bg-line/30 border-b-2 border-transparent"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Check list</div>
                  <div className="font-display text-base text-ink truncate">{t.label}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`font-mono text-xs ${complete ? "text-green-700 font-semibold" : "text-ink"}`}>
                    {coveredItems}/{totalItems} · {pct}%
                  </span>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-brand" /> : <ChevronRight className="w-4 h-4 text-ink-muted" />}
                </div>
              </div>
              <div className="mt-2 h-1.5 bg-line rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${complete ? "bg-green-600" : "bg-brand"}`}
                  style={{ width: `${pct}%` }}
                  data-testid={`checklist-progress-${t.key}-${dossierId}`}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Contenuto tab attivo */}
      {openTab && (
        <div className="p-4 border-t border-line" data-testid={`checklist-body-${openTab}-${dossierId}`}>
          {tabs
            .filter((t) => t.key === openTab)
            .map((t) => (
              <ChecklistBlock
                key={t.key}
                agevolazione={t.key}
                sections={t.sections}
                progress={t.progress}
                docsByKey={docsByKey}
                dossierId={dossierId}
                embedded
              />
            ))}
        </div>
      )}
    </div>
  );
}

function ChecklistBlock({ title, agevolazione, sections, progress, docsByKey, dossierId, embedded = false }) {
  const { totalItems, coveredItems, pct } = progress;
  const wrapperCls = embedded ? "" : "bg-white border border-line rounded p-4";
  return (
    <div className={wrapperCls} data-testid={`checklist-${agevolazione}-${dossierId}`}>
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{title}</div>
            <div className="font-display text-lg text-ink mt-1">
              {coveredItems}/{totalItems} voci coperte
              <span className="ml-2 text-sm text-ink-muted font-sans font-normal">({pct}%)</span>
            </div>
          </div>
          <div className="flex-1 sm:max-w-sm">
            <div className="h-2 bg-line rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${pct === 100 ? "bg-green-600" : "bg-brand"}`}
                style={{ width: `${pct}%` }}
                data-testid={`checklist-progress-${agevolazione}-${dossierId}`}
              />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {sections.map((section) => {
          const sectionCovered = section.items.filter((it) => (docsByKey[it.key] || []).length > 0).length;
          return (
            <div key={section.key} className="border border-line rounded">
              <div className="px-3 py-2 bg-line/40 flex items-center justify-between">
                <div className="text-sm font-semibold text-ink">{section.title}</div>
                <div className="text-xs font-mono text-ink-muted">
                  {sectionCovered}/{section.items.length}
                </div>
              </div>
              <ul className="divide-y divide-line">
                {section.items.map((it) => {
                  const uploaded = docsByKey[it.key] || [];
                  const hasFile = uploaded.length > 0;
                  return (
                    <li
                      key={it.key}
                      className="px-3 py-2 flex items-start gap-3"
                      data-testid={`checklist-item-${dossierId}-${it.key}`}
                    >
                      <div className="pt-0.5 flex-shrink-0">
                        {hasFile ? (
                          <CheckCircle2 className="w-4 h-4 text-brand" />
                        ) : (
                          <Circle className="w-4 h-4 text-ink-muted/50" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${hasFile ? "text-ink" : "text-ink-muted"}`}>{it.label}</div>
                        {it.template === "modulo_raccolta_dati" && (
                          <div className="mt-2">
                            <a
                              href={`${API}/dossier/${dossierId}/modulo-raccolta-dati`}
                              data-testid={`admin-download-template-${dossierId}`}
                              className="inline-flex items-center gap-1.5 text-xs text-brand hover:text-brand-dark"
                            >
                              <Download className="w-3 h-3" /> Scarica modulo precompilato (.pdf)
                            </a>
                          </div>
                        )}
                        {hasFile && (
                          <ul className="mt-1.5 space-y-1">
                            {uploaded.map((doc) => (
                              <li key={doc.doc_id} className="flex items-center gap-2 text-xs">
                                <FileText className="w-3 h-3 text-ink-muted flex-shrink-0" />
                                <a
                                  href={`${API}/admin/dossiers/${dossierId}/doc/${doc.doc_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-ink hover:text-brand truncate flex-1"
                                  title={doc.filename}
                                  data-testid={`admin-doc-${doc.doc_id}`}
                                >
                                  {doc.filename}
                                </a>
                                <span className="font-mono text-[10px] text-ink-muted whitespace-nowrap">
                                  {(doc.size / 1024).toFixed(0)} KB
                                </span>
                                <a
                                  href={`${API}/admin/dossiers/${dossierId}/doc/${doc.doc_id}`}
                                  className="text-ink-muted hover:text-brand"
                                  title="Scarica"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {!hasFile && (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted/70 flex-shrink-0 mt-1">
                          mancante
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
